import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, WritableSignal } from '@angular/core';
import { ApiResponse } from '../../../../../common/models/api-response.model';
import { ENDPOINTS } from '../../../../../common/constants/endpoints.constants';
import { Option } from '../../../../../common/models/option.model';
import { RequestPosition } from '../../../../../common/models/request-details.model';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ContributorsService {
  private readonly http = inject(HttpClient);

  getPositionsWithContributors(
    flowId: string
  ): Observable<ApiResponse<RequestPosition>> {
    const url =
      ENDPOINTS.Application.Contributor.getPositionsWithContributors.replace(
        '{flowId}',
        flowId
      );
    return this.http.get<ApiResponse<RequestPosition>>(url);
  }

  getStepContributors(
    flowId: string,
    stepId: number | string,
    filters: {
      name?: string;
      page?: number;
      perPage?: number;
    }
  ): Observable<ApiResponse<Option>> {
    const filterMappings = {
      name: 'filters[search]',
      page: 'page',
      perPage: 'per_page',
    };

    const url = ENDPOINTS.Application.Contributor.getStepContributors
      .replace('{flowId}', flowId)
      .replace('{stepId}', String(stepId));

    let params = new HttpParams();

    Object.entries(filterMappings).forEach(([key, paramName]) => {
      if (filters[key as keyof typeof filters]) {
        params = params.append(
          paramName,
          filters[key as keyof typeof filters] as string
        );
      }
    });
    return this.http.get<ApiResponse<Option>>(url, { params });
  }

  addContributor(flowId: string, stepId: number | string, userId: string) {
    const url = ENDPOINTS.Application.Contributor.addContributor
      .replace('{flowId}', flowId)
      .replace('{stepId}', String(stepId));
    return this.http.post(url, { user_id: userId });
  }

  removeContributor(
    flowId: string,
    stepId: number | string,
    contributorId: string
  ) {
    const url = ENDPOINTS.Application.Contributor.removeContributor
      .replace('{flowId}', flowId)
      .replace('{stepId}', String(stepId))
      .replace('{contributorId}', contributorId);
    return this.http.delete(url);
  }

  updateStepContributors(
    positions: WritableSignal<RequestPosition[]>,
    stepId: number | string,
    updateContributors: (contributors: Option[]) => Option[]
  ) {
    positions.update(currentPositions => {
      const normalizedStepId = String(stepId);

      const positionIndex = currentPositions.findIndex(position =>
        position.steps.some(
          step => String(step?.details?.id) === normalizedStepId
        )
      );

      if (positionIndex === -1) {
        return currentPositions;
      }

      const position = currentPositions[positionIndex];
      const stepIndex = position.steps.findIndex(
        step => String(step?.details?.id) === normalizedStepId
      );

      if (stepIndex === -1) {
        return currentPositions;
      }

      const targetStep = position.steps[stepIndex];
      const nextContributors = updateContributors(
        targetStep.details?.contributors ?? []
      );

      if (nextContributors === targetStep.details?.contributors) {
        return currentPositions;
      }

      const nextContributorsCount =
        nextContributors.length >
        (targetStep.details?.contributors ?? [])?.length
          ? 1
          : -1;

      const nextSteps = [...position.steps];

      nextSteps[stepIndex] = {
        ...targetStep,
        details: {
          ...targetStep?.details,
          contributors: nextContributors,
          contributors_count:
            (targetStep?.details?.contributors_count ?? 0) +
            nextContributorsCount,
        },
      };

      const nextPositions = [...currentPositions];
      nextPositions[positionIndex] = {
        ...position,
        steps: nextSteps,
      };

      return nextPositions;
    });
  }

  getContributorKey(contributor: Option) {
    return String(contributor.value ?? contributor.id);
  }
}
